import { useState } from 'react'
import { Source, Layer, Marker } from 'react-map-gl'
import { metersToPixelsAtMaxZoom } from '../utils/helpers/map.helpers'
import { useAgamiMarkerStyle } from '../hooks/useAgamiMarkerStyle'
import AgamiMarkerIcon from '../components/AgamiMarkerIcon'

interface XpSpotDraggableMarkerProps {
  position: {
    id: string
    longitude: number
    latitude: number
    radius: number
    deadband: number
  }
  onDragStart?: (event: { lngLat: { lng: number; lat: number } }) => void
  onDragEnd?: (event: { lngLat: { lng: number; lat: number } }) => void
}

const XpSpotDraggableMarker: React.FC<XpSpotDraggableMarkerProps> = ({
  position,
  onDragStart,
  onDragEnd,
}) => {
  const [isDragging, setIsDragging] = useState(false)

  const { radiusStyle, deadbandStyle } = useAgamiMarkerStyle()

  const center = [position.longitude, position.latitude]

  const feature = {
    type: 'Feature' as const,
    id: `${position.id}_feature`,
    properties: {},
    geometry: {
      type: 'Point' as const,
      coordinates: center,
    },
  }

  const radiusCircleSize = metersToPixelsAtMaxZoom(position.radius, position.latitude)

  const deadbandCircleSize = metersToPixelsAtMaxZoom(position.radius + position.deadband, position.latitude)

  const radiusLayerStyle = {
    id: `${position.id}_radius_circle`,
    type: 'circle' as const,
    paint: {
      'circle-radius': {
        stops: [
          [0, 0],
          [20, radiusCircleSize],
        ],
        base: 2,
      },
      'circle-stroke-color': radiusStyle.color,
      'circle-stroke-opacity': radiusStyle.opacity,
      'circle-stroke-width': radiusStyle.weight,
      'circle-stroke-color-transition': {
        duration: 5000,
        delay: 0,
      },
      'circle-color': radiusStyle.fillColor,
      'circle-opacity': radiusStyle.fillOpacity,
      'circle-color-transition': {
        duration: 1000,
        delay: 0,
      },
      'circle-pitch-alignment': 'map' as const,
    },
  }

  const deadbandLayerStyle = {
    id: `${position.id}_deadband_circle`,
    type: 'circle' as const,
    paint: {
      'circle-radius': {
        stops: [
          [0, 0],
          [20, deadbandCircleSize],
        ],
        base: 2,
      },
      'circle-radius-transition': {
        duration: 1000,
        delay: 0,
      },
      'circle-stroke-color': deadbandStyle.color,
      'circle-stroke-opacity': deadbandStyle.opacity,
      'circle-stroke-width': deadbandStyle.weight,
      'circle-stroke-color-transition': {
        duration: 1000,
        delay: 0,
      },
      'circle-color': deadbandStyle.fillColor,
      'circle-opacity': deadbandStyle.fillOpacity,
      'circle-color-transition': {
        duration: 1000,
        delay: 0,
      },
      'circle-pitch-alignment': 'map' as const,
    },
  }

  return (
    <>
      {!isDragging && (
        <Source id={`${position.id}_source`} type="geojson" data={feature}>
          <Layer {...radiusLayerStyle} />
          <Layer {...deadbandLayerStyle} />
        </Source>
      )}

      <Marker
        longitude={position.longitude}
        latitude={position.latitude}
        anchor="center"
        draggable
        onDragStart={(evt) => {
          setIsDragging(true)
          if (onDragStart) {
            onDragStart(evt)
          }
        }}
        onDragEnd={(evt) => {
          setIsDragging(false)
          if (onDragEnd) {
            onDragEnd(evt)
          }
        }}
      >
        <AgamiMarkerIcon />
      </Marker>
    </>
  )
}

export default XpSpotDraggableMarker
